import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import driverLicenseFrontPlaceholder from '../../images/kyc/driver_license_front.png';
import driverLicenseBackPlaceholder from '../../images/kyc/driver_license_back.png';
import mynumberFrontPlaceholder from '../../images/kyc/mynumber_front.png';
import passportFrontPlaceholder from '../../images/kyc/passport_front.png';
import residenceCardFrontPlaceholder from '../../images/kyc/residence_card_front.png';
import residenceCardBackPlaceholder from '../../images/kyc/residence_card_back.png';
import { useAuthUserContext } from '../../providers';
import { useApi } from '../../hooks/useApi';
import { Kyc, ApiKyc } from '../../models/Kyc';
import { UserType } from '../../types';
import '../../styles/_mypage_kyc.scss';
import Spinner from '../../components/Spinner';
import { useTitle } from '../../hooks/useTitle';

const mimeTypeToExtension: { [key: string]: string } = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/bmp': '.bmp',
};

const dataURLtoFile = (dataurl: string, filename: string): File | null => {
  if (!dataurl || !dataurl.startsWith('data:')) return null;
  const arr = dataurl.split(',');
  if (arr.length < 2) return null;
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) return null;
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  const extension = mimeTypeToExtension[mime] || '.bin';
  return new File([u8arr], `${filename}${extension}`, { type: mime });
}

const MypageKyc: React.FC = () => {
  const [ kyc, setKyc ] = useState<Kyc | null>(null);
  const { get, post, loading } = useApi();
  const [ birthDate, setBirthDate ] = useState('');
  const [ frontImagePreview, setFrontImagePreview ] = useState<string | null>(null);
  const [ backImagePreview, setBackImagePreview ] = useState<string | null>(null);
  const authUser = useAuthUserContext().user;
  const isDonee = authUser?.userType === UserType.Donee;

  useTitle('Ton Bébé | 本人確認');

  const isEditable = kyc && ['DRAFT', 'REMANDED'].includes(kyc.status);

  const frontFileInputRef = React.useRef<HTMLInputElement>(null);
  const backFileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, setPreview: React.Dispatch<React.SetStateAction<string | null>>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };


  const getPlaceholderImage = (kycType: string | undefined, isFront: boolean) => {
    if (isFront) {
      switch (kycType) {
        case 'DRIVER_LICENSE':
          return driverLicenseFrontPlaceholder;
        case 'MYNUMBER':
          return mynumberFrontPlaceholder;
        case 'PASSPORT':
          return passportFrontPlaceholder;
        case 'RESIDENCE_CARD':
          return residenceCardFrontPlaceholder;
        default:
          return driverLicenseFrontPlaceholder;
      }
    } else {
      switch (kycType) {
        case 'RESIDENCE_CARD':
          return residenceCardBackPlaceholder;
        default:
          return driverLicenseBackPlaceholder;
      }
    }
  };

  const fetchKyc = useCallback(() => {
    get({
      endpoint: 'api/v1/kycs/show',
      onSuccess: (data) => {
        const apiKyc: ApiKyc = data.data;
        const kyc = new Kyc(apiKyc);
        setKyc(kyc);
        setBirthDate(data.data?.birth_date || '');
        if (kyc.docImgFileFrontUrl) {
          setFrontImagePreview(kyc.docImgFileFrontUrl);
        }
        if (kyc.docImgFileBackUrl) {
          setBackImagePreview(kyc.docImgFileBackUrl);
        }
      },
      onError: (err) => {
        console.error(err);
      }
    });
  }, [ get ]);

  useEffect(() => {
    fetchKyc();
  }, [ fetchKyc ]);

  const validateForSubmit = (): boolean => {
    if (!birthDate) {
      toast.error('生年月日を入力してください。');
      return false;
    }
    if (!kyc?.kycType) {
      toast.error('本人確認書類の種類を選択してください。');
      return false;
    }
    if (!frontImagePreview) {
      toast.error('表面の画像をアップロードしてください。');
      return false;
    }
    if (['DRIVER_LICENSE', 'RESIDENCE_CARD'].includes(kyc.kycType) && !backImagePreview) {
      toast.error('裏面の画像をアップロードしてください。');
      return false;
    }
    return true;
  };

  const updateKyc = (nextStatus: 'SUBMITTED' | 'DRAFT') => {
    if (!kyc) return;
    if (nextStatus === 'SUBMITTED' && !validateForSubmit()) return;

    const formData = new FormData();
    formData.append('next_status', nextStatus);
    formData.append('birth_date', birthDate);
    formData.append('kyc_type', kyc.kycType);

    if (frontImagePreview && frontImagePreview.startsWith('data:')) {
      const frontFile = dataURLtoFile(frontImagePreview, 'doc_img_file_front');
      if (frontFile) {
        formData.append('doc_img_file_front', frontFile);
        formData.append('is_doc_img_file_front_changed', 'true');
      }
    }

    if (backImagePreview && backImagePreview.startsWith('data:')) {
      const backFile = dataURLtoFile(backImagePreview, 'doc_img_file_back');
      if (backFile) {
        formData.append('doc_img_file_back', backFile);
        formData.append('is_doc_img_file_back_changed', 'true');
      }
    }

    post({
      endpoint: 'api/v1/kycs/me',
      formData,
      method: 'PATCH',
      onSuccess: () => {
        const act = nextStatus === 'SUBMITTED' ? '提出' : '保存';
        toast.success(`${ act }しました。`);
        fetchKyc();
      },
      onError: (err) => {
        toast.error("保存に失敗しました。時間をおいて再度お試しください。");
      }
    });
  };

  return (
    <main className="sub">
      { loading && <Spinner overlay={ true } color={ isDonee ? "pink" : "blue" } /> }
      <section className={ "mypage_kyc " + (isDonee ? "pink" : "") }>
        <h2 className="main_title">
          <span>本人確認</span>
        </h2>
        <div className="form">
          {kyc?.status === 'REMANDED' && kyc.comments && (
            <div className="border_box pink">
              <span className="txt_pink alert_msg">
                {kyc.comments.split('\n').map((line, idx) => (
                  <React.Fragment key={ idx }>
                    { line }<br />
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
          <form>
            <dl className={ "graf_list " + (isDonee ? "pink" : "") }>
              <dt>登録状況</dt>
              <dd>
                { kyc ? kyc.statusForUserText : '未提出' }
              </dd>
              <dt>生年月日</dt>
              <dd>
                { isEditable ? (
                  <input type="date" value={ birthDate } onChange={ e => setBirthDate(e.target.value)} />
                ) : (
                  birthDate.replace(/-/g, '/')
                )}
              </dd>
              <dt>本人確認書類の種類</dt>
              <dd>
                { isEditable ? (
                  <div className="select select01">
                    <select
                      value={ kyc?.kycType || '' }
                      onChange={ e => setKyc(prev => prev ? prev.set("kycType", e.target.value) : null) }
                    >
                      <option value="">選択してください</option>
                      <option value="DRIVER_LICENSE">運転免許証</option>
                      <option value="MYNUMBER">マイナンバーカード</option>
                      <option value="RESIDENCE_CARD">在留カード</option>
                    </select>
                  </div>
                ) : (
                  kyc ? kyc.kycTypeText : ''
                )}
              </dd>
              {isEditable && (
                <>
                  <dt>表面の画像</dt>
                  <dd>
                    <div className="file-input-container">
                      <div
                          className="image-preview-box"
                          style={{
                            backgroundImage: `url(${frontImagePreview || getPlaceholderImage(kyc?.kycType, true)})`,
                          }}
                          onClick={() => frontFileInputRef.current?.click()} // Make the div clickable
                        >
                          {!frontImagePreview && (
                            <div
                              className="image-preview-overlay"
                            >
                              表面
                            </div>
                          )}
                        </div>
                      <input
                        type="file"
                        ref={frontFileInputRef}
                        onChange={(e) => handleFileChange(e, setFrontImagePreview)}
                        disabled={!kyc || loading}
                        className="hidden-file-input"
                        accept="image/*"
                      />
                    </div>
                  </dd>
                  { (kyc?.kycType === 'DRIVER_LICENSE' || kyc?.kycType === 'RESIDENCE_CARD' || kyc?.kycType === '') && (
                    <>
                      <dt>裏面の画像</dt>
                      <dd>
                        <div className="file-input-container">
                          <div
                            className="image-preview-box"
                            style={{
                              backgroundImage: `url(${backImagePreview || getPlaceholderImage(kyc?.kycType, false)})`,
                            }}
                            onClick={() => backFileInputRef.current?.click()} // Make the div clickable
                          >
                            {!backImagePreview && (
                              <div
                                className="image-preview-overlay"
                              >
                                裏面
                              </div>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={backFileInputRef}
                            onChange={(e) => handleFileChange(e, setBackImagePreview)}
                            disabled={!kyc || loading}
                            className="hidden-file-input"
                            accept="image/*"
                          />
                        </div>
                      </dd>
                    </>
                  )}
                </>
              )}
            </dl>
            <ul className="box">
              <h2>【注意事項】</h2>
              <li>＊本人確認書類は券面の情報が全て読み取れるようにしてください。</li>
              <li>＊ご提出いただく書類を他のユーザーが閲覧することはありません。</li>
              <li>＊書類の信憑性を確認できない場合はアカウントを停止する場合があります。</li>
              <li>＊書類の<strong className="text-red">偽造/変造は犯罪</strong>です。刑法により処罰される可能性があります。</li>
              <li>※個人情報の取り扱いについては<Link to="/privacy">プライバシーポリシー</Link>のページをご覧ください。</li>
            </ul>
            {isEditable && (
              <ul className="btnarea">
                <li>
                  <input
                    type="button"
                    value="提出する"
                    className={ isDonee ? "w_pinkbtn" : "w_bluebtn" }
                    disabled={!kyc || loading}
                    onClick={() => updateKyc('SUBMITTED')}
                  />
                </li>
                <li>
                  <input
                    type="button"
                    value="一次保存する"
                    className={ isDonee ? "w_pinkbtn" : "w_bluebtn" }
                    disabled={!kyc || loading}
                    onClick={() => updateKyc('DRAFT')}
                  />
                </li>
              </ul>
            )}
          </form>
        </div>
      </section>
    </main>
  );
};

export default MypageKyc;
